import React, { FunctionComponent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Track } from '@justgiving/react-tracking';
import gql from 'graphql-tag';

import { MembersListQuery } from '../../../types/__generated__';
import PATHS from '../../constants/paths';
import { PageMetaContext } from '../../context';
import { useFullPageQuery, useNotNullableContext } from '../../hooks';
import { makePhotoUrlFromAbsoluteUrl } from '../../utils/makePhotoUrl';
import InviteMembersModal from '../InviteMembersModal/InviteMembersModal';
import IsPageOwner from '../IsPageOwner/IsPageOwner';
import LoadMoreButton from '../LoadMoreButton/LoadMoreButton';
import MemberDetails from '../MemberDetails/MemberDetails';
import PageSection from '../PageSection/PageSection';

import './MemberList.scss';

export const DOCUMENT = gql`
  query MembersList($after: String, $type: PageType!, $slug: Slug!) {
    page(type: $type, slug: $slug) {
      id
      legacyId
      membershipPolicy
      relationships {
        members(first: 10, after: $after) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          nodes {
            legacyId
            owner {
              name
              avatar
            }
            slug
            title
            status
            targetWithCurrency {
              value
            }
            donationSummary {
              totalAmount {
                value
                currencyCode
              }
            }
          }
        }
      }
    }
  }
`;

export type MemberListProps = {
  listRef: React.RefObject<HTMLElement>;
};

const MembersList: FunctionComponent<MemberListProps> = props => {
  const { t } = useTranslation('common', { useSuspense: false });
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const { currentUrl } = useNotNullableContext(PageMetaContext);

  const { listRef } = props;

  const { loading, error, data, fetchMore } = useFullPageQuery<
    MembersListQuery
  >(DOCUMENT, {
    notifyOnNetworkStatusChange: true,
  });

  if (
    error ||
    !data ||
    !data.page ||
    !data.page.relationships ||
    !data.page.relationships.members
  ) {
    return null;
  }

  const {
    page: { relationships, membershipPolicy, legacyId },
  } = data;
  const members = relationships && relationships.members;
  const membersCount = (members && members.totalCount) || 0;
  const pageInfo = members && members.pageInfo;

  const shareURL = `${currentUrl}?invite=true`;

  // TODO is members avatar absolute?

  async function handleFetchMore() {
    await fetchMore({
      variables: { after: pageInfo.endCursor },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (
          !previousResult.page ||
          !previousResult.page.relationships ||
          !previousResult.page.relationships.members ||
          !fetchMoreResult ||
          !fetchMoreResult.page ||
          !fetchMoreResult.page.relationships ||
          !fetchMoreResult.page.relationships.members
        ) {
          return previousResult;
        }

        return {
          page: {
            ...previousResult.page,
            relationships: {
              ...fetchMoreResult.page.relationships,
              members: {
                ...fetchMoreResult.page.relationships.members,
                nodes: [
                  ...previousResult.page.relationships.members.nodes,
                  ...fetchMoreResult.page.relationships.members.nodes,
                ],
              },
            },
          },
        };
      },
    });
  }

  return (
    <PageSection
      title={t('members.title')}
      hasBorder
      count={membersCount}
      sectionRef={listRef}
    >
      <ul className="jg-pages-teams-members__list">
        {members &&
          members.nodes.map((member, index: number) => (
            <MemberDetails
              key={member.legacyId}
              fundraisingPageIndex={index}
              fundraisingPagesCount={membersCount}
              name={(member.owner && member.owner.name) || ''}
              picture={
                (member.owner &&
                  member.owner.avatar &&
                  makePhotoUrlFromAbsoluteUrl(member.owner.avatar)) ||
                undefined
              }
              fundraisingPageShortName={member.slug}
              fundraisingPageName={member.title}
              fundraisingPageStatus={member.status || 'Inactive'}
              fundraisingPageTargetAmount={member.targetWithCurrency?.value}
              fundraisingPageRaisedAmount={
                member.donationSummary?.totalAmount?.value ?? 0
              }
              fundraisingPageCurrencyCode={
                member.donationSummary?.totalAmount?.currencyCode ?? 'GBP'
              }
            />
          ))}
      </ul>
      {pageInfo.hasNextPage && (
        <LoadMoreButton
          className="jg-pages-teams-members-buttons"
          loading={loading}
          onClick={handleFetchMore}
          trackingData={{
            action_name: 'click',
            subtype: 'button',
            event: 'click',
            event_value: 'show more',
            sub_section: 'members',
          }}
        >
          <Trans i18nKey="members.showMore">
            Show more
            <span className="jg-sr-only"> members</span>
          </Trans>
        </LoadMoreButton>
      )}
      {membershipPolicy === 'open' && (
        <Track data-qa="join-team-members">
          {track => (
            <a
              data-qa="join-button"
              href={`${PATHS.JOIN_URL}${legacyId}`}
              className="jg-btn jg-btn--themed jg-pages-teams-members-buttons"
              onClick={() =>
                track({
                  action_name: 'link click',
                  subtype: 'button',
                  event_value: 'join the team',
                  sub_section: 'team members',
                })
              }
            >
              <Trans i18nKey="members.join">Join the team</Trans>
            </a>
          )}
        </Track>
      )}
      <IsPageOwner>
        <button
          data-qa="invite-members-button"
          className="jg-btn jg-btn--themed jg-pages-teams-members-buttons"
          onClick={() => {
            setInviteModalOpen(true);
          }}
          type="button"
        >
          <Trans i18nKey="members.invite">Invite team members</Trans>
        </button>
        <InviteMembersModal
          isModalOpen={inviteModalOpen}
          onCloseModal={() => {
            setInviteModalOpen(false);
          }}
          shareURL={shareURL}
        />
      </IsPageOwner>
    </PageSection>
  );
};

export default MembersList;
